// The target's screen, letterboxed, with the agent's marks over it. While a person has taken over, clicking it locks
// the pointer: moves, buttons, the wheel and keys go to the target until Esc releases the lock.
import { useEffect, useRef, useState } from "react";
import type { Live } from "./live";

type Pt = [number, number];
const isPt = (v: unknown): v is Pt => Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === "number");
const BUTTONS = ["left", "middle", "right"];

export function Screen({ live }: { live: Live }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<Pt | null>(null);
  const [locked, setLocked] = useState(false);
  const human = live.state === "human";
  const absolute = live.pointer === "absolute";
  const latest = useRef(live);
  latest.current = live;
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    live.onFrame((bmp) => {
      const c = canvas.current;
      if (!c) return;
      if (c.width !== bmp.width || c.height !== bmp.height) {
        c.width = bmp.width; c.height = bmp.height; setSize([bmp.width, bmp.height]);
      }
      c.getContext("2d")!.drawImage(bmp, 0, 0);
      bmp.close();
    });
  }, [live]);

  // take-over input: only while the pointer is locked to the screen
  useEffect(() => {
    if (!human || absolute) { if (document.pointerLockElement) document.exitPointerLock(); return; }
    let dx = 0, dy = 0, raf = 0, wheelAt = 0;
    const flush = () => { raf = 0; if (dx || dy) { live.send({ t: "m", dx, dy }); dx = dy = 0; } };
    const isLocked = () => document.pointerLockElement === canvas.current;
    const move = (e: MouseEvent) => {
      if (!isLocked()) return;
      dx += e.movementX; dy += e.movementY;
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const btn = (down: boolean) => (e: MouseEvent) => {
      if (!isLocked()) return;
      e.preventDefault(); flush();
      live.send({ t: "b", button: BUTTONS[e.button] ?? "left", down });
    };
    const wheel = (e: WheelEvent) => {
      if (!isLocked()) return;
      e.preventDefault();
      const now = performance.now();
      if (now - wheelAt < 40 || !e.deltaY) return;              // one notch per 40 ms, as a mouse wheel turns
      wheelAt = now;
      live.send({ t: "w", dy: Math.sign(e.deltaY) });
    };
    const key = (down: boolean) => (e: KeyboardEvent) => {
      if (!isLocked() || e.code === "Escape") return;           // Esc releases the lock (the browser's own rule)
      e.preventDefault();
      if (!e.repeat || down) live.send({ t: "k", code: e.code, down });
    };
    const lockChange = () => setLocked(isLocked());
    const down = btn(true), up = btn(false), kd = key(true), ku = key(false);
    document.addEventListener("mousemove", move);
    document.addEventListener("mousedown", down);
    document.addEventListener("mouseup", up);
    document.addEventListener("wheel", wheel, { passive: false });
    document.addEventListener("keydown", kd);
    document.addEventListener("keyup", ku);
    document.addEventListener("pointerlockchange", lockChange);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mousedown", down);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("wheel", wheel);
      document.removeEventListener("keydown", kd);
      document.removeEventListener("keyup", ku);
      document.removeEventListener("pointerlockchange", lockChange);
    };
  }, [human, absolute, live]);

  // VNC alone uses framebuffer pixels. Focus, not pointer lock, gates keys;
  // blur/Esc/handback releases every input this canvas has held.
  useEffect(() => {
    const c = canvas.current;
    if (!human || !absolute || !c) return;
    const keys = new Set<string>(), buttons = new Set<number>();
    let wheelAt = 0;
    const send = (m: Record<string, unknown>) => latest.current.send(m);
    const active = () => document.activeElement === c;
    const position = (e: MouseEvent, clamp = false) => {
      const full = latest.current.screen;
      if (!full) return false; // never mistake the downscaled JPEG for desktop pixels
      const r = c.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      if (!clamp && (x < 0 || y < 0 || x >= 1 || y >= 1)) return false;
      return send({ t: "ma", x: Math.max(0, Math.min(full[0] - 1, Math.floor(x * full[0]))),
        y: Math.max(0, Math.min(full[1] - 1, Math.floor(y * full[1]))) });
    };
    const release = () => {
      for (const code of keys) send({ t: "k", code, down: false });
      for (const b of buttons) send({ t: "b", button: BUTTONS[b], down: false });
      keys.clear(); buttons.clear(); setFocused(false);
    };
    const move = (e: MouseEvent) => { if (active()) position(e, buttons.size > 0); };
    const down = (e: MouseEvent) => {
      if (e.target !== c || !BUTTONS[e.button]) return;
      c.focus(); setFocused(true); e.preventDefault();
      if (position(e)) { buttons.add(e.button); send({ t: "b", button: BUTTONS[e.button], down: true }); }
    };
    const up = (e: MouseEvent) => {
      if (!buttons.has(e.button)) return;
      position(e, true);
      send({ t: "b", button: BUTTONS[e.button], down: false }); buttons.delete(e.button);
    };
    const wheel = (e: WheelEvent) => {
      if (!active() || e.target !== c) return;
      e.preventDefault();
      const now = performance.now();
      if (now - wheelAt < 40 || !e.deltaY || !position(e)) return;
      wheelAt = now; send({ t: "w", dy: Math.sign(e.deltaY) });
    };
    const key = (down: boolean) => (e: KeyboardEvent) => {
      if (!active()) return;
      e.preventDefault();
      if (e.code === "Escape") { c.blur(); release(); return; }
      if (down) keys.add(e.code); else keys.delete(e.code);
      send({ t: "k", code: e.code, down });
    };
    const kd = key(true), ku = key(false);
    const hidden = () => { if (document.hidden) { c.blur(); release(); } };
    document.addEventListener("mousemove", move);
    document.addEventListener("mousedown", down);
    document.addEventListener("mouseup", up);
    document.addEventListener("wheel", wheel, { passive: false });
    document.addEventListener("keydown", kd); document.addEventListener("keyup", ku);
    document.addEventListener("visibilitychange", hidden);
    c.addEventListener("blur", release); window.addEventListener("blur", release);
    return () => {
      release();
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mousedown", down);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("wheel", wheel);
      document.removeEventListener("keydown", kd); document.removeEventListener("keyup", ku);
      document.removeEventListener("visibilitychange", hidden);
      c.removeEventListener("blur", release); window.removeEventListener("blur", release);
    };
  }, [human, absolute]);

  const full = live.screen ?? size;
  const m = live.marks;
  const boxes = Array.isArray(m.boxes) ? (m.boxes as number[][]).filter((b) => b.length === 4) : [];
  const tol = isPt(m.tol) ? m.tol : null;

  return (
    <div className={"screen" + (human ? " human" : "")}>
      <div className="frame" style={{ maxWidth: "100%", maxHeight: "100%" }}>
        <canvas
          ref={canvas}
          tabIndex={human && absolute ? 0 : -1}
          onClick={() => { if (human && !absolute && !locked) canvas.current?.requestPointerLock(); }}
          onContextMenu={(e) => e.preventDefault()}
        />
        {full && (
          <svg className="marks" viewBox={`0 0 ${full[0]} ${full[1]}`} preserveAspectRatio="none">
            {boxes.map((b, i) => (
              <rect key={i} x={b[0]} y={b[1]} width={b[2]} height={b[3]} fill="none"
                stroke="var(--warn)" strokeWidth={2} vectorEffect="non-scaling-stroke" opacity={0.8} />
            ))}
            {isPt(m.target) && tol && (
              <rect x={m.target[0] - tol[0]} y={m.target[1] - tol[1]} width={2 * tol[0]} height={2 * tol[1]}
                fill="none" stroke="var(--accent)" strokeWidth={1} vectorEffect="non-scaling-stroke" strokeDasharray="4 3" />
            )}
            {isPt(m.target) && (
              <circle cx={m.target[0]} cy={m.target[1]} r={14} fill="none" stroke="var(--accent)"
                strokeWidth={2} vectorEffect="non-scaling-stroke" />
            )}
            {isPt(m.cursor) && (
              <circle cx={m.cursor[0]} cy={m.cursor[1]} r={5} fill="var(--accent)" opacity={0.85} />
            )}
          </svg>
        )}
      </div>
      {human && (
        <div className="chip">
          <span className="dot run" />
          {(absolute ? focused : locked) ? <>You have control · <span className="muted">Esc releases the mouse</span></>
            : <>You have control · <span className="muted">click the screen to use mouse and keys</span></>}
        </div>
      )}
      {!size && <Waiting live={live} />}
    </div>
  );
}

function Waiting({ live }: { live: Live }) {
  const text: Record<string, [string, string]> = {
    connecting: ["Connecting", "Opening the target…"],
    busy: ["In use", `${live.holder ?? "Someone"} is driving this target. Try another, or ask an admin to release it.`],
    closed: live.error ? ["Unavailable", live.error] : ["Disconnected", "Reconnecting…"],
    error: ["Unavailable", live.error ?? ""],
  };
  const [h, p] = text[live.state] ?? (live.error ? ["Unavailable", live.error] : ["Waiting for the screen", ""]);
  return (
    <div className="empty"><div><h2>{h}</h2><div>{p}</div></div></div>
  );
}
